import { apiClient } from './apiClient';

/**
 * Get company slug from current URL path
 */
function getCompanySlug(): string {
  const pathname = window.location.pathname;
  const segments = pathname.split('/').filter(Boolean);
  // First segment is company (unless it's a reserved route)
  const reserved = ['admin', 'manager', 'agent', 'app', 'superadmin'];
  if (segments.length > 0 && !reserved.includes(segments[0])) {
    return segments[0];
  }
  // Fallback to localStorage
  return localStorage.getItem('currentCompany') || 'test';
}

/**
 * Get Gmail connection status (unified endpoint)
 */
export async function getGmailStatus(): Promise<{
  connected: boolean;
  hasRefreshToken: boolean;
  expiresAt: number | null;
  grantedScopes: string[];
  needsReconnect: boolean;
  reason: 'NOT_CONNECTED' | 'NEEDS_RECONNECT' | 'INSUFFICIENT_SCOPES' | 'SCOPES_UNKNOWN' | 'OK';
  missingScopes?: string[];
}> {
  const company = getCompanySlug();
  try {
    const { data } = await apiClient.post(`/api/${company}/rpc/gmail/status`, {}, {
      withCredentials: true,
    });
    
    if (data?.ok && data?.data) {
      return data.data;
    }
    
    // Fallback if response format is unexpected
    return {
      connected: false,
      hasRefreshToken: false,
      expiresAt: null,
      grantedScopes: [],
      needsReconnect: true,
      reason: 'NOT_CONNECTED',
    };
  } catch (err: any) {
    console.error('[emailApi] getGmailStatus error:', err);
    return {
      connected: false,
      hasRefreshToken: false,
      expiresAt: null,
      grantedScopes: [],
      needsReconnect: true,
      reason: 'NOT_CONNECTED',
    };
  }
}

/**
 * List inbox threads
 */
export async function getInbox(params: {
  pageSize?: number;
  pageToken?: string;
  query?: string;
  search?: string;
}) {
  const company = getCompanySlug();
  try {
    const { data } = await apiClient.post(`/api/${company}/rpc/email/inbox/list`, params, {
      withCredentials: true,
    });
    
    // Debug log
/*     console.log('[emailApi] getInbox raw response:', {
      hasData: !!data,
      hasOk: data?.ok,
      hasDataKey: 'data' in (data || {}),
      dataKeys: data ? Object.keys(data) : [],
      threadsInData: data?.data?.threads?.length || 0,
      threadsInRoot: data?.threads?.length || 0,
    }); */
    
    if (data?.ok && 'data' in data) {
      const result = data.data;
/*       console.log('[emailApi] Returning data.data:', {
        hasThreads: !!result?.threads,
        threadsCount: result?.threads?.length || 0,
      }); */
      return result;
    }
/*     console.log('[emailApi] Returning data directly:', {
      hasThreads: !!data?.threads,
      threadsCount: data?.threads?.length || 0,
    }); */
    return data;
  } catch (err: any) {
    // Handle standardized error codes from backend
    const errorCode = err?.response?.data?.code;
    const errorStatus = err?.response?.status;
    
    if (errorCode === 'NOT_CONNECTED' || errorCode === 'NEEDS_RECONNECT' || errorCode === 'INSUFFICIENT_SCOPES') {
      const error = new Error(err?.response?.data?.error || err?.message || 'Gmail connection error');
      (error as any).code = errorCode;
      throw error;
    }
    
    // Handle legacy error codes
    if (errorCode === 'GOOGLE_SCOPES_MISSING' || (errorStatus === 403 && errorCode === 'INSUFFICIENT_SCOPES')) {
      const error = new Error('Google authorization missing required Gmail scopes. Please reconnect.');
      (error as any).code = 'INSUFFICIENT_SCOPES';
      throw error;
    }
    
    // Handle 401 errors
    if (errorStatus === 401) {
      const error = new Error(err?.response?.data?.error || 'Gmail authentication failed. Please reconnect.');
      (error as any).code = 'NEEDS_RECONNECT';
      throw error;
    }
    
    // Handle 500 errors gracefully - likely email service not configured
    if (errorStatus === 500) {
      // Return empty result instead of throwing
      return { threads: [], pagination: { hasMore: false } };
    }
    
    // Re-throw other errors
    throw err;
  }
}

/**
 * Get a single thread with all messages
 */
export async function getThread(threadId: string) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/thread`, { threadId }, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Get unread email count
 */
export async function getUnreadCount(): Promise<number> {
  const company = getCompanySlug();
  try {
    const { data } = await apiClient.post(`/api/${company}/rpc/email/unread-count`, {}, {
      withCredentials: true,
    });
    
    if (data?.ok && data?.data?.unreadCount !== undefined) {
      return data.data.unreadCount;
    }
    return 0;
  } catch (err: any) {
    // CRITICAL FIX: Handle 503 (Service Unavailable) - circuit breaker is open
    // Silently handle timeout, connection, and service unavailable errors - don't log
    const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('exceeded');
    const isConnectionError = err?.code === 'ERR_CONNECTION_REFUSED' || err?.message?.includes('ERR_CONNECTION_REFUSED');
    const isServiceUnavailable = err?.response?.status === 503;
    
    if (!isTimeout && !isConnectionError && !isServiceUnavailable) {
      // Only log non-timeout/connection/service-unavailable errors
      console.error('[emailApi] getUnreadCount error:', err);
    }
    return 0;
  }
}

// Types
export interface EmailThread {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  snippet: string;
  lastMessageDate: Date | string;
  unreadCount: number;
  messageCount: number;
}

export interface EmailMessage {
  id: string;
  threadId: string;
  from: string;
  to: string[];
  cc?: string[];
  subject: string;
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: Array<{
    filename: string;
    mimeType: string;
    size: number;
    attachmentId: string;
  }>;
  sentAt: Date | string;
  direction: 'incoming' | 'outgoing';
  isRead: boolean;
  labelIds?: string[];
}

export interface EmailThreadDetail {
  id: string;
  subject: string;
  messages: EmailMessage[];
}

export interface EmailDraft {
  id: string;
  draftId: string;
  messageId: string;
  threadId: string | null;
  to: string[];
  cc: string[];
  bcc?: string[];
  subject: string;
  bodyHtml: string | null;
  bodyText: string | null;
  updatedAt: Date | string;
}

/**
 * Send an email
 */
export async function sendEmail(emailData: {
  to: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  threadId?: string;
  attachments?: Array<{
    filename: string;
    data: string; // base64
    mimeType: string;
    size: number;
  }>;
}) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/send`, emailData, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * List drafts
 */
export async function listDrafts(params?: {
  pageSize?: number;
  pageToken?: string;
}) {
  const company = getCompanySlug();
  try {
    const { data } = await apiClient.post(`/api/${company}/rpc/email/drafts/list`, params || {}, {
      withCredentials: true,
    });
    
    if (data?.ok && 'data' in data) {
      return data.data;
    }
    return data;
  } catch (err: any) {
    // Handle scope errors
    if (err?.response?.status === 403 && err?.response?.data?.code === 'GOOGLE_SCOPES_MISSING') {
      const error = new Error('Google authorization missing required Gmail scopes. Please reconnect.');
      (error as any).code = 'GOOGLE_SCOPES_MISSING';
      throw error;
    }
    throw err;
  }
}

/**
 * Get a single draft
 */
export async function getDraft(draftId: string) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/drafts/get`, { draftId }, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Create a draft
 */
export async function createDraft(draftData: {
  to?: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  threadId?: string;
}) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/drafts/create`, draftData, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Update a draft
 */
export async function updateDraft(draftId: string, draftData: {
  to?: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  threadId?: string;
}) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/drafts/update`, { draftId, ...draftData }, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Delete a draft
 */
export async function deleteDraft(draftId: string) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/drafts/delete`, { draftId }, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Send a draft email
 */
export async function sendDraft(draftId: string) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/drafts/send`, { draftId }, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * List sent emails
 */
export async function listSent(params?: {
  pageSize?: number;
  pageToken?: string;
  query?: string;
}) {
  const company = getCompanySlug();
  try {
    const { data } = await apiClient.post(`/api/${company}/rpc/email/sent/list`, params || {}, {
      withCredentials: true,
    });
    
    if (data?.ok && 'data' in data) {
      return data.data;
    }
    return data;
  } catch (err: any) {
    // Handle scope errors
    if (err?.response?.status === 403 && err?.response?.data?.code === 'GOOGLE_SCOPES_MISSING') {
      const error = new Error('Google authorization missing required Gmail scopes. Please reconnect.');
      (error as any).code = 'GOOGLE_SCOPES_MISSING';
      throw error;
    }
    throw err;
  }
}

/**
 * List spam emails
 */
export async function listSpam(params?: {
  pageSize?: number;
  pageToken?: string;
  query?: string;
}) {
  const company = getCompanySlug();
  try {
    const { data } = await apiClient.post(`/api/${company}/rpc/email/spam/list`, params || {}, {
      withCredentials: true,
    });
    
    if (data?.ok && 'data' in data) {
      return data.data;
    }
    return data;
  } catch (err: any) {
    // Handle scope errors
    if (err?.response?.status === 403 && err?.response?.data?.code === 'GOOGLE_SCOPES_MISSING') {
      const error = new Error('Google authorization missing required Gmail scopes. Please reconnect.');
      (error as any).code = 'GOOGLE_SCOPES_MISSING';
      throw error;
    }
    throw err;
  }
}

/**
 * Mark message(s) as spam
 */
export async function markAsSpam(messageIds: string | string[]) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/spam/mark`, { messageIds }, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Remove message(s) from spam
 */
export async function removeFromSpam(messageIds: string | string[]) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/spam/remove`, { messageIds }, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Delete message(s)
 */
export async function deleteMessages(messageIds: string | string[]) {
  const company = getCompanySlug();
  const { data } = await apiClient.post(`/api/${company}/rpc/email/messages/delete`, { messageIds }, {
    withCredentials: true,
  });
  
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Disconnect Gmail (hard disconnect - deletes all tokens)
 */
export async function disconnectGmail(): Promise<{ ok: boolean }> {
  const company = getCompanySlug();
  try {
    const { data } = await apiClient.post(`/api/${company}/rpc/gmail/disconnect`, {}, {
      withCredentials: true,
    });
    
    if (data?.ok) {
      return { ok: true };
    }
    
    throw new Error(data?.error || 'Failed to disconnect Gmail');
  } catch (err: any) {
    console.error('[emailApi] disconnectGmail error:', err);
    throw err;
  }
}


